import { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUrlState } from "@/hooks/useUrlState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { TestBadge, TaskBadge, EmptyState, ErrorState, ListSkeleton } from "@/components/common";
import { subscribeTaskReviewChanged } from "@/lib/taskReview";
import { DuplicateTaskDialog, submitWithDuplicateGuard } from "@/components/DuplicateTaskDialog";
import { CheckSquare, Check, SkipForward, PlusCircle, RotateCcw, Search, X } from "lucide-react";

const PRIORITY_BADGE = { high: "bg-red-500/15 text-red-600 dark:text-red-400", normal: "bg-sky-500/15 text-sky-600 dark:text-sky-400", low: "bg-muted text-muted-foreground" };

// 2026-09 Phase 2 (D3) — UX-only mirror of the backend's real
// core.LEADERSHIP_ROLES: routine employee-created tasks shouldn't offer
// Leadership as an assignee at all. The backend (routers_extras.py's
// _assert_can_assign_tasks_to) is the actual authority either way — a
// crafted request naming a Leadership user_id is rejected there
// regardless of what this list filters out here.
const LEADERSHIP_ROLES = new Set(["manager", "supervisor", "admin"]);

// 2026-09 P4 — real, paginated canonical views (task_service.query_tasks).
// "Skipped"/"Cancelled" are reachable but deliberately not mixed into
// Completed — the API's own `status` param, not a client-side guess.
const VIEWS = [
  { key: "mine", label: "My Open", params: { status: "open", scope: "own" } },
  { key: "due_today", label: "Due Today", params: { due_today: "true", scope: "own" } },
  { key: "overdue", label: "Overdue", params: { overdue: "true", scope: "own" } },
  { key: "completed", label: "Completed", params: { status: "completed", scope: "own" } },
  { key: "skipped", label: "Skipped", params: { status: "skipped", scope: "own" } },
  { key: "team", label: "Team", params: { status: "open", scope: "team" }, leadershipOnly: true },
  // 2026-09 stabilization (Workstream C) — provider/system failures (voice
  // dispatch infrastructure failures, results that never arrived, ...) are
  // an OPERATIONAL queue, deliberately NOT part of any employee's overdue
  // or open counts above.
  { key: "system", label: "System / provider failures", params: { status: "open", scope: "team", queue: "system_failure" }, leadershipOnly: true },
];

const PAGE_SIZE = 25;

export default function TasksPage() {
  const { hasCap, user } = useAuth();
  const location = useLocation();
  const [urlState, setUrlState] = useUrlState({ view: "mine", page: 1, sort: "oldest", includeTest: false });
  const { view, page, sort, includeTest } = urlState;
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);   // R7: a failed load is an error + Retry, not "No tasks here"
  const [counts, setCounts] = useState(null);
  const [skipTask, setSkipTask] = useState(null);
  const [skipReason, setSkipReason] = useState("");
  const [reopenTask, setReopenTask] = useState(null);
  const [reopenReason, setReopenReason] = useState("");
  const [overrideReasonTask, setOverrideReasonTask] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [completeTask, setCompleteTask] = useState(null);
  const [completeNote, setCompleteNote] = useState("");
  const [submitTask, setSubmitTask] = useState(null);
  const [submitNote, setSubmitNote] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [nt, setNt] = useState({ title: "", description: "", priority: "normal", due_at: "", assignee: "me" });
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactOpps, setContactOpps] = useState([]);
  const [selectedOppId, setSelectedOppId] = useState("");

  const canAssignAny = hasCap("tasks.assign");
  const canAssignPeer = hasCap("tasks.assign_peer");
  const canSeeTeam = canAssignAny || ["admin", "manager", "supervisor"].includes(user?.role);
  const visibleViews = VIEWS.filter((v) => !v.leadershipOnly || canSeeTeam);

  // The exact URL this page is at right now — a task-launched action (Log
  // call / Verify payment / …) passes this through so Customer 360 can
  // return here afterward via its own Back control, never a second Complete
  // click needed once the employee comes back (P4 Problem 9/10).
  const returnTo = encodeURIComponent(location.pathname + location.search);

  const load = useCallback(() => {
    const activeView = VIEWS.find((v) => v.key === view) || VIEWS[0];
    const p = new URLSearchParams({ ...activeView.params, page: String(page), limit: String(PAGE_SIZE) });
    if (view === "overdue") p.set("sort", sort);
    // TEST records are excluded from every employee count/list unless the
    // viewer explicitly includes them (the state is shown in the URL and in
    // the count strip below — never silently persisted).
    if (includeTest) p.set("include_test", "true");
    api.get(`/tasks?${p.toString()}`).then((r) => { setData(r.data); setLoadError(null); }).catch((e) => {
      setData(null);   // never keep another view's rows under an error
      setLoadError(errDetail(e, "The task list didn't load"));
    });
    const countScope = (view === "team" || view === "system") ? "team" : "own";
    api.get("/tasks/counts", { params: { scope: countScope, include_test: includeTest } })
      .then((r) => setCounts(r.data)).catch(() => setCounts(null));
  }, [view, page, sort, includeTest]);

  useEffect(() => { load(); }, [load]);
  // 2026-09 Workstream J Section 5 — a consultation outcome / call log saved
  // elsewhere (Customer 360, Calls) reconciles tasks server-side; while this
  // page is mounted it refetches the SAME way the task-review panel already
  // does (lib/taskReview's shared event bus), never a poll.
  useEffect(() => subscribeTaskReviewChanged(() => load()), [load]);
  useEffect(() => {
    if (newOpen && (canAssignAny || canAssignPeer)) {
      api.get("/users").then((r) => setUsers(r.data.users.filter((u) => u.is_active))).catch(() => {});
    }
  }, [newOpen, canAssignAny, canAssignPeer]);

  useEffect(() => {
    if (custQuery.trim().length < 2) { setCustResults([]); return; }
    const t = setTimeout(() => api.get(`/search?q=${encodeURIComponent(custQuery)}`)
      .then((r) => setCustResults(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [custQuery]);

  useEffect(() => {
    if (!selectedContact) { setContactOpps([]); setSelectedOppId(""); return; }
    api.get(`/opportunities?contact_id=${selectedContact.contact_id}&lifecycle_status=active`)
      .then((r) => { setContactOpps(r.data); setSelectedOppId(r.data.length === 1 ? r.data[0].opportunity_id : ""); })
      .catch(() => setContactOpps([]));
  }, [selectedContact]);

  const assignableUsers = canAssignAny ? users : users.filter((u) => !LEADERSHIP_ROLES.has(u.role));

  const [dupConflict, setDupConflict] = useState(null);
  const [dupBusy, setDupBusy] = useState(false);
  const resetNewTask = () => {
    setNewOpen(false); setNt({ title: "", description: "", priority: "normal", due_at: "", assignee: "me" });
    setSelectedContact(null); setCustQuery(""); setCustResults([]); setContactOpps([]); setSelectedOppId(""); setDupConflict(null);
  };
  const submitNewTaskBody = (extra) => api.post("/ops/tasks", {
    title: nt.title, description: nt.description, priority: nt.priority,
    due_at: nt.due_at ? `${nt.due_at}:00Z` : null,
    assignee_user_ids: nt.assignee === "me" ? [] : [nt.assignee],
    contact_id: selectedContact?.contact_id || null,
    opportunity_id: selectedOppId || null,
    ...extra,
  });
  const createTask = async () => {
    try {
      const r = await submitWithDuplicateGuard(submitNewTaskBody);
      if (!r.ok) { setDupConflict(r.conflict); return; }
      toast.success("Task created");
      resetNewTask();
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const resolveDuplicate = async (decision, reason) => {
    setDupBusy(true);
    try {
      await submitNewTaskBody({ duplicate_decision: decision, duplicate_reason: reason });
      toast.success(decision === "cancel" ? "Not created" : decision === "use_and_reschedule"
        ? "Existing task rescheduled" : decision === "replace" ? "Existing task replaced" : "Both tasks kept");
      resetNewTask();
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setDupBusy(false); }
  };

  const ACT_SUCCESS_LABEL = { complete: "Task completed", reopen: "Task reopened",
    skip: "Task skipped", claim: "Task claimed — you're now the assignee",
    submit_for_approval: "Submitted for Leadership review",
    withdraw_submission: "Submission withdrawn" };
  const act = async (taskId, action, reason, note) => {
    try {
      await api.post(`/tasks/${taskId}/action`, { action, reason, note });
      toast.success(ACT_SUCCESS_LABEL[action] || "Task updated");
      load();
    } catch (e) {
      toast.error(errDetail(e));
    }
  };

  // 2026-09 P4 — the intelligent Complete action (Problem 9): each row
  // already carries the backend's own structured action state
  // (can_complete/completion_mode/action_label/action_route), computed by
  // task_service.compute_task_action_state — never re-derived here.
  const isOwnTask = (t) => t.owner_user_id && t.owner_user_id === user?.user_id;
  const completeClick = (t) => {
    if (t.completion_mode === "system_verified" && t.can_complete) {
      // 2026-09 P4 Round 2 — Leadership completing a task that isn't
      // theirs (owned by someone else, or unassigned) still needs a
      // reason, even when the real evidence already exists — same rule
      // as every other override.
      if (!isOwnTask(t)) {
        setOverrideReasonTask(t); setOverrideReason("");
        return;
      }
      act(t.task_id, "complete");
      return;
    }
    if (t.completion_mode === "system_verified" && !t.can_complete) {
      if (t.action_route) {
        window.location.assign(`${t.action_route}?return_to=${returnTo}&origin_task_id=${t.task_id}`);
      } else {
        toast.error(t.missing_evidence || "This task can't be completed yet.");
      }
      return;
    }
    if (t.completion_mode === "leadership_approval") {
      // 2026-09 P4.1 — SalesOS can't independently verify this task, so
      // there is no direct Complete at all: only "submit for Leadership
      // review". review_state === "pending_leadership_review" is handled
      // entirely by the row's own action area (Withdraw), never reaches
      // this click handler.
      setSubmitTask(t); setSubmitNote(t.clarification_note ? "" : "");
      return;
    }
    setCompleteTask(t); setCompleteNote("");
  };

  const withdrawClick = (t) => act(t.task_id, "withdraw_submission");

  const items = data?.items || [];
  const total = data?.total ?? 0;
  const hasMore = !!data?.has_more;

  return (
    <div className="space-y-4" data-testid="tasks-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Tasks</h1>
          <p className="text-sm text-muted-foreground">Mandatory tasks gate stage progression — skips need a reason and are audited.</p>
        </div>
        <Button className="ml-auto h-9" data-testid="task-new-btn" onClick={() => setNewOpen(true)}>
          <PlusCircle className="h-4 w-4 mr-1.5" /> New task
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Tabs value={view} onValueChange={(v) => setUrlState({ view: v, page: 1 })}>
          <TabsList className="flex-wrap h-auto justify-start gap-0.5">
            {visibleViews.map((v) => (
              <TabsTrigger key={v.key} data-testid={`tasks-tab-${v.key}`} value={v.key}>{v.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {view === "overdue" && (
          <Select value={sort} onValueChange={(v) => setUrlState({ sort: v, page: 1 })}>
            <SelectTrigger data-testid="tasks-overdue-sort" className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="oldest">Oldest overdue first</SelectItem>
              <SelectItem value="newest">Newest overdue first</SelectItem>
            </SelectContent>
          </Select>
        )}
        {canSeeTeam && (
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer" data-testid="tasks-include-test-label">
            <Checkbox data-testid="tasks-include-test" checked={includeTest}
              onCheckedChange={(v) => setUrlState({ includeTest: v === true, page: 1 })} />
            Include TEST
          </label>
        )}
        {data && (
          <span className="text-xs text-muted-foreground ml-auto" data-testid="tasks-count">
            {total} task{total === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {counts?.employee && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid="tasks-count-strip">
          <span data-testid="tasks-count-employee">
            Employee tasks: <b className="text-foreground">{counts.employee.open}</b> open ·{" "}
            <b className="text-[hsl(var(--danger))]">{counts.employee.overdue}</b> overdue
          </span>
          {canSeeTeam && (
            <span data-testid="tasks-count-system">
              System / provider failures (not employee delinquency):{" "}
              <b className="text-foreground">{counts.system_failure.open}</b> open ·{" "}
              <b className="text-foreground">{counts.system_failure.overdue}</b> overdue
            </span>
          )}
          <span data-testid="tasks-count-population">
            Business {counts.population.business.employee_overdue} overdue · TEST {counts.population.test.employee_overdue} overdue
            {includeTest ? " (TEST included above)" : " (TEST excluded above)"}
          </span>
        </div>
      )}

      <Card>
        {loadError && !data ? <ErrorState testId="tasks-error" title="Couldn't load tasks" message={loadError} onRetry={load} />
          : !data ? <ListSkeleton rows={3} testId="tasks-loading" />
          : items.length === 0 ? <EmptyState icon={CheckSquare} title="No tasks here" hint="Stage tasks and follow-ups will appear automatically." />
            : (
              <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead className="hidden md:table-cell">Customer</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((t) => {
                    const overdue = t.overdue;
                    return (
                      <TableRow key={t.task_id} data-testid={`task-row-${t.task_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{t.title}</span>
                            {t.is_mandatory && <span className="text-[10px] uppercase text-[hsl(var(--warning))] font-semibold">Mandatory</span>}
                            {t.due_today && <Badge variant="outline" className="text-[9px]">Due today</Badge>}
                            <TestBadge show={t.is_test_record} />
                          </div>
                          {t.description && <p className="text-xs text-muted-foreground truncate max-w-md">{t.description}</p>}
                          {t.status === "open" && t.completion_mode === "system_verified" && !t.can_complete && (
                            <p className="text-[11px] text-muted-foreground mt-0.5">{t.missing_evidence}</p>
                          )}
                          {t.status === "open" && t.review_state === "pending_leadership_review" && (
                            <p className="text-[11px] text-[hsl(var(--warning))] mt-0.5" data-testid={`task-review-state-${t.task_id}`}>
                              Pending Leadership Review — employee action submitted, awaiting decision
                            </p>
                          )}
                          {t.status === "open" && t.review_state === "clarification_requested" && (
                            <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5" data-testid={`task-review-state-${t.task_id}`}>
                              Clarification requested: {t.clarification_note}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {t.contact_id ? (
                            <Link to={`/customers/${t.contact_id}?return_to=${returnTo}`} className="text-sm hover:underline">{t.contact_name}</Link>
                          ) : "—"}
                        </TableCell>
                        <TableCell className={overdue ? "text-[hsl(var(--danger))] text-sm font-medium" : "text-sm text-muted-foreground"}>
                          {fmtIST(t.effective_due_at || t.due_at, { time: false })}
                          {!t.due_at && <span className="text-[10px] text-muted-foreground"> (default)</span>}
                          {overdue && " · overdue"}
                        </TableCell>
                        <TableCell><TaskBadge status={t.status} />{t.priority && t.priority !== "normal" && <Badge className={`ml-1 ${PRIORITY_BADGE[t.priority] || ""}`}>{t.priority}</Badge>}</TableCell>
                        <TableCell className="text-right">
                          {t.status === "open" ? (
                            // 2026-09 P4 Round 2 (Problem 3) — an unassigned
                            // task is never a free-for-all: an ordinary
                            // employee sees ONLY Claim until they take it;
                            // Leadership (who can override any task anyway)
                            // sees Claim alongside the normal actions.
                            !t.owner_user_id && !canSeeTeam ? (
                              <Button data-testid={`task-claim-${t.task_id}`} size="sm" variant="outline"
                                onClick={() => act(t.task_id, "claim")}>
                                Claim
                              </Button>
                            ) : (
                            <div className="flex gap-1.5 justify-end">
                              {!t.owner_user_id && (
                                <Button data-testid={`task-claim-${t.task_id}`} size="sm" variant="ghost"
                                  onClick={() => act(t.task_id, "claim")}>
                                  Claim
                                </Button>
                              )}
                              {t.review_state === "pending_leadership_review" ? (
                                <Button data-testid={`task-withdraw-${t.task_id}`} size="sm" variant="ghost"
                                  onClick={() => withdrawClick(t)}>
                                  Withdraw
                                </Button>
                              ) : (
                                <Button data-testid={`task-complete-${t.task_id}`} size="sm" variant="outline" onClick={() => completeClick(t)}>
                                  <Check className="h-3.5 w-3.5 mr-1" />
                                  {t.completion_mode === "system_verified" && !t.can_complete
                                    ? (t.action_label || "Go to evidence") : (t.action_label && t.completion_mode === "leadership_approval" ? t.action_label : "Complete")}
                                </Button>
                              )}
                              {/* 2026-09 P4.1 Section G — Skip is hidden for
                                  system_verified/leadership_approval/Mandatory
                                  tasks unless the viewer is Leadership (the
                                  backend enforces this either way; hiding it
                                  here is just so an ordinary employee never
                                  clicks into a guaranteed 403). */}
                              {(canSeeTeam || (!t.is_mandatory && t.completion_mode === "informational_acknowledgement"))
                                && t.review_state !== "pending_leadership_review" && (
                                <Button data-testid={`task-skip-${t.task_id}`} size="sm" variant="ghost" onClick={() => { setSkipTask(t); setSkipReason(""); }}>
                                  <SkipForward className="h-3.5 w-3.5 mr-1" /> Skip
                                </Button>
                              )}
                            </div>
                            )
                          ) : ["completed", "skipped"].includes(t.status) && (
                            <Button data-testid={`task-reopen-${t.task_id}`} size="sm" variant="ghost"
                              onClick={() => {
                                // Reopening your OWN task needs no reason;
                                // Leadership reopening someone else's
                                // (or an unassigned) task does — same rule
                                // as every other override, prompted here.
                                if (t.owner_user_id && t.owner_user_id === user?.user_id) {
                                  act(t.task_id, "reopen");
                                } else {
                                  setReopenTask(t); setReopenReason("");
                                }
                              }}>
                              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reopen
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between p-3 border-t">
                <span className="text-xs text-muted-foreground">
                  Page {page} — {items.length} of {total}
                </span>
                <div className="flex gap-1.5">
                  <Button data-testid="tasks-prev-page" variant="outline" size="sm" disabled={page <= 1}
                    onClick={() => setUrlState({ page: Math.max(1, page - 1) })}>Previous</Button>
                  <Button data-testid="tasks-next-page" variant="outline" size="sm" disabled={!hasMore}
                    onClick={() => setUrlState({ page: page + 1 })}>Next</Button>
                </div>
              </div>
              </>
            )}
      </Card>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="sm:max-w-md" data-testid="task-new-dialog">
          <DialogHeader><DialogTitle className="font-heading">New task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Title</Label><Input data-testid="task-new-title" value={nt.title} onChange={(e) => setNt({ ...nt, title: e.target.value })} /></div>
            <div><Label className="text-xs">Description</Label><Textarea data-testid="task-new-desc" value={nt.description} onChange={(e) => setNt({ ...nt, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Priority</Label>
                <Select value={nt.priority} onValueChange={(v) => setNt({ ...nt, priority: v })}>
                  <SelectTrigger data-testid="task-new-priority" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="high">High</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Deadline</Label><Input data-testid="task-new-due" type="datetime-local" className="h-9" value={nt.due_at} onChange={(e) => setNt({ ...nt, due_at: e.target.value })} /></div>
            </div>
            {(canAssignAny || canAssignPeer) && (
              <div><Label className="text-xs">Assignee {canAssignAny ? "(Leadership can assign to anyone)" : "(you can assign to another employee)"}</Label>
                <Select value={nt.assignee} onValueChange={(v) => setNt({ ...nt, assignee: v })}>
                  <SelectTrigger data-testid="task-new-assignee" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="me">Myself</SelectItem>
                    {assignableUsers.filter((u) => u.user_id !== user?.user_id).map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Customer (optional)</Label>
              {!selectedContact ? (
                <>
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                    <Input data-testid="task-new-customer-search" className="h-9 pl-7" placeholder="Search customer…"
                      value={custQuery} onChange={(e) => setCustQuery(e.target.value)} />
                  </div>
                  {custResults.length > 0 && (
                    <div className="space-y-1 max-h-32 overflow-y-auto rounded border p-1">
                      {custResults.map((r) => (
                        <button key={r.contact_id} type="button" data-testid={`task-new-customer-${r.contact_id}`}
                          onClick={() => { setSelectedContact(r); setCustQuery(""); setCustResults([]); }}
                          className="w-full text-left p-1.5 rounded hover:bg-muted/50 text-sm">
                          {r.full_name} <span className="font-mono text-xs text-muted-foreground">{r.customer_id}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2 rounded border p-1.5 text-sm" data-testid="task-new-customer-selected">
                  <span className="font-medium">{selectedContact.full_name}</span>
                  <span className="font-mono text-xs text-muted-foreground">{selectedContact.customer_id}</span>
                  <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0 ml-auto" onClick={() => setSelectedContact(null)}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
            {selectedContact && contactOpps.length > 1 && (
              <div className="space-y-1.5">
                <Label className="text-xs">Opportunity / Product</Label>
                <Select value={selectedOppId} onValueChange={setSelectedOppId}>
                  <SelectTrigger data-testid="task-new-opportunity" className="h-9"><SelectValue placeholder="Which product…" /></SelectTrigger>
                  <SelectContent>
                    {contactOpps.map((o) => <SelectItem key={o.opportunity_id} value={o.opportunity_id}>{o.product_name || o.product_code}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {selectedContact && contactOpps.length === 0 && (
              <p className="text-[11px] text-muted-foreground">This customer has no active opportunity — the task will link to the customer only.</p>
            )}
          </div>
          <DialogFooter><Button data-testid="task-new-save" disabled={!nt.title.trim()} onClick={createTask}>Create task</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <DuplicateTaskDialog conflict={dupConflict} busy={dupBusy} onChoose={resolveDuplicate} onCancel={() => setDupConflict(null)} />

      <Dialog open={!!skipTask} onOpenChange={(o) => !o && setSkipTask(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Skip task</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">A reason is required and recorded in the audit trail.</p>
          <Textarea data-testid="task-skip-reason" placeholder="Why is this task being skipped?" value={skipReason} onChange={(e) => setSkipReason(e.target.value)} />
          <DialogFooter>
            <Button
              data-testid="task-skip-confirm"
              disabled={!skipReason.trim()}
              onClick={() => { act(skipTask.task_id, "skip", skipReason); setSkipTask(null); }}
            >
              Skip with reason
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!overrideReasonTask} onOpenChange={(o) => !o && setOverrideReasonTask(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Complete task — Leadership override</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            The real evidence already exists, but this isn't your own task — a reason is required
            and recorded in the audit trail.
          </p>
          <Textarea data-testid="task-override-reason" placeholder="Why are you completing this on their behalf?"
            value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
          <DialogFooter>
            <Button
              data-testid="task-override-confirm"
              disabled={!overrideReason.trim()}
              onClick={() => { act(overrideReasonTask.task_id, "complete", overrideReason); setOverrideReasonTask(null); }}
            >
              Complete with reason
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reopenTask} onOpenChange={(o) => !o && setReopenTask(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Reopen task</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This isn't your own task — a reason is required and recorded in the audit trail.
          </p>
          <Textarea data-testid="task-reopen-reason" placeholder="Why is this task being reopened?"
            value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} />
          <DialogFooter>
            <Button
              data-testid="task-reopen-confirm"
              disabled={!reopenReason.trim()}
              onClick={() => { act(reopenTask.task_id, "reopen", reopenReason); setReopenTask(null); }}
            >
              Reopen with reason
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!completeTask} onOpenChange={(o) => !o && setCompleteTask(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Complete task</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            SalesOS can't independently verify this task — describe what was actually done.
          </p>
          <Textarea data-testid="task-complete-note" placeholder="What did you do to complete this?"
            value={completeNote} onChange={(e) => setCompleteNote(e.target.value)} />
          <DialogFooter>
            <Button
              data-testid="task-complete-confirm"
              disabled={!completeNote.trim()}
              onClick={() => { act(completeTask.task_id, "complete", isOwnTask(completeTask) ? null : completeNote, completeNote); setCompleteTask(null); }}
            >
              Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!submitTask} onOpenChange={(o) => !o && setSubmitTask(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Submit completion for approval</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            SalesOS can't independently verify this task — it stays incomplete until
            Leadership reviews and approves it. Describe what was actually done.
          </p>
          {submitTask?.clarification_note && (
            <div className="rounded border border-amber-500/40 bg-amber-500/10 p-2 text-xs">
              Leadership asked for clarification: {submitTask.clarification_note}
            </div>
          )}
          <Textarea data-testid="task-submit-note" placeholder="What did you do to complete this?"
            value={submitNote} onChange={(e) => setSubmitNote(e.target.value)} />
          <DialogFooter>
            <Button
              data-testid="task-submit-confirm"
              disabled={!submitNote.trim()}
              onClick={() => { act(submitTask.task_id, "submit_for_approval", null, submitNote); setSubmitTask(null); }}
            >
              Submit for approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
